import { COMPARE } from '../utils/constants';
import { readCompareItems, removeCompareItem } from '../lib/compareStorage';
import fetchProductView from '../lib/fetchProductView';
import initProductCards from '../components/product-card';

// Compare page (sections/compare.liquid, templates/page.compare.json). Each
// stored selection is fetched as its product.compare alternate template
// (snippets/compare-column.liquid) into one of the section's columns; the
// rest keep their "add another product" placeholder. Removing a column
// updates storage, puts the placeholder back and moves that column to the
// end so the remaining products close up.

const BUSY_ATTR = 'aria-busy';
// A feature row is a label cell and a value cell
const CELLS_PER_ROW = 2;

// Back to the collection the customer came from; a shared or bookmarked
// compare page has no same-site history, so that case goes to all products
const isSameOrigin = (url) => {
  try {
    return new URL(url).origin === location.origin;
  } catch {
    return false;
  }
};

const goBack = (buttonEl) => {
  if (isSameOrigin(document.referrer) && window.history.length > 1) {
    window.history.back();
    return;
  }
  window.location.assign(buttonEl.dataset.fallbackUrl);
};

const resetColumn = (columnEl, placeholderTemplateEl) => {
  columnEl.replaceChildren(placeholderTemplateEl.content.cloneNode(true));
};

// The status paragraph stays in the DOM so its text changes are announced
const setStatus = (statusEl, text) => {
  if (statusEl) statusEl.textContent = text;
};

// Every product lists its own features in its own order, so the rows are
// rebuilt to one shared order. Rows are keyed by label plus occurrence so a
// product listing the same label twice keeps both; a product missing a row
// gets a "not available" cell cloned from an existing row so styling matches
const buildMissingRow = (label, text, [templateLabelEl, templateValueEl]) => {
  const labelEl = templateLabelEl.cloneNode(false);
  labelEl.textContent = label;
  const valueEl = templateValueEl.cloneNode(false);
  valueEl.textContent = text;
  return [labelEl, valueEl];
};

const toRowKey = (label, occurrence) => `${occurrence} ${label}`;

const readRows = (productEl) => {
  const rows = new Map();
  const seenCounts = new Map();
  productEl.querySelectorAll('[data-compare-label]').forEach((labelEl) => {
    const label = labelEl.textContent.trim();
    const occurrence = seenCounts.get(label) ?? 0;
    seenCounts.set(label, occurrence + 1);
    rows.set(toRowKey(label, occurrence), { label, cells: [labelEl, labelEl.nextElementSibling] });
  });
  return rows;
};

const alignRows = (columnsEl, notAvailableText) => {
  const productEls = [...columnsEl.querySelectorAll('[data-compare-product]')];
  if (!productEls.length) return;
  const rowMaps = productEls.map(readRows);
  // Union of rows in first-seen order
  const rowEntries = new Map();
  rowMaps.forEach((rows) => rows.forEach(({ label }, key) => {
    if (!rowEntries.has(key)) rowEntries.set(key, label);
  }));
  const templateRow = rowMaps[0].values().next().value.cells;

  productEls.forEach((productEl, index) => {
    const rowsEl = productEl.querySelector('[data-compare-rows]');
    if (!rowsEl) return;
    const cells = [...rowEntries].flatMap(([key, label]) => rowMaps[index].get(key)?.cells ?? buildMissingRow(label, notAvailableText, templateRow));
    rowsEl.replaceChildren(...cells);
  });
  // Header block plus a label and value row per feature
  columnsEl.style.setProperty('--compare-rows', String(1 + rowEntries.size * CELLS_PER_ROW));
};

const loadColumn = async (columnEl, item, placeholderTemplateEl) => {
  try {
    const viewDoc = await fetchProductView(item.handle, COMPARE.VIEW);
    const productEl = viewDoc.querySelector('[data-compare-product]');
    if (!productEl) throw new Error('Compare view has no product markup');
    columnEl.replaceChildren(productEl);
  } catch (error) {
    // A deleted or unpublished product 404s: drop it so it stops reappearing
    console.error('Compare page could not load a product:', error);
    removeCompareItem(item.id);
    resetColumn(columnEl, placeholderTemplateEl);
  }
};

const initComparePage = () => {
  const pageEl = document.querySelector('[data-compare-page]');
  if (!pageEl || pageEl.dataset.comparePageInit) return;
  pageEl.dataset.comparePageInit = 'true';

  const columnsEl = pageEl.querySelector('[data-compare-columns]');
  const columnEls = [...pageEl.querySelectorAll('[data-compare-column]')];
  const placeholderTemplateEl = pageEl.querySelector('[data-compare-placeholder]');
  const statusEl = pageEl.querySelector('[data-compare-status]');
  if (!columnsEl || !columnEls.length || !placeholderTemplateEl) return;

  const { loadingText = '', emptyText = '', notAvailableText = '' } = pageEl.dataset;

  const loadItems = async () => {
    const items = readCompareItems().slice(0, columnEls.length);
    if (!items.length) {
      setStatus(statusEl, emptyText);
      return;
    }
    setStatus(statusEl, loadingText);
    pageEl.setAttribute(BUSY_ATTR, 'true');
    await Promise.all(items.map((item, index) => loadColumn(columnEls[index], item, placeholderTemplateEl)));
    pageEl.removeAttribute(BUSY_ATTR);
    alignRows(columnsEl, notAvailableText);
    setStatus(statusEl, readCompareItems().length ? '' : emptyText);
    // The columns carry the card's add-to-bag button and hooks
    initProductCards();
  };

  const removeColumn = (removeEl) => {
    const items = removeCompareItem(removeEl.dataset.compareRemove);
    const columnEl = removeEl.closest('[data-compare-column]');
    resetColumn(columnEl, placeholderTemplateEl);
    // The emptied column goes to the end so the remaining products close up,
    // and rows only the removed product had are dropped
    columnsEl.append(columnEl);
    alignRows(columnsEl, notAvailableText);
    // The Remove button went with the column: focus the link that replaced it
    columnEl.querySelector('a')?.focus();
    if (!items.length) setStatus(statusEl, emptyText);
  };

  pageEl.addEventListener('click', (event) => {
    const backEl = event.target.closest('[data-compare-back]');
    if (backEl) {
      goBack(backEl);
      return;
    }
    const removeEl = event.target.closest('[data-compare-remove]');
    if (removeEl) removeColumn(removeEl);
  });

  loadItems();
};

export default initComparePage;
